import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardService from "../services/DashboardService";
import type { WidgetSpec } from "../models/Widget";
import type { WidgetData } from "../models/WidgetData";

/**
 * Datos de los widgets persistidos: carga inicial en batch y auto-refresh individual según el
 * refreshSeconds de cada widget (los que no lo definen solo se refrescan manualmente).
 */
export function useWidgetData(workspaceId: string, widgets: WidgetSpec[]) {
  const service = useMemo(() => new DashboardService(), []);
  const [dataById, setDataById] = useState<Record<string, WidgetData>>({});
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());
  const widgetsRef = useRef(widgets);
  widgetsRef.current = widgets;

  const merge = useCallback((results: WidgetData[]) => {
    setDataById((prev) => {
      const next = { ...prev };
      for (const r of results) next[r.widgetId] = r;
      return next;
    });
  }, []);

  const refresh = useCallback(
    async (widgetIds?: string[]) => {
      const ids = widgetIds ?? widgetsRef.current.map((w) => w.id);
      if (ids.length === 0) return;
      setRefreshingIds((prev) => new Set([...prev, ...ids]));
      try {
        const response = await service.getData(workspaceId, { widgetIds: ids });
        merge(response.widgets);
      } catch {
        // El error por-widget viaja dentro de la respuesta; un fallo de red se refleja
        // dejando los datos previos y quitando el spinner.
      } finally {
        setRefreshingIds((prev) => {
          const next = new Set(prev);
          for (const id of ids) next.delete(id);
          return next;
        });
      }
    },
    [merge, service, workspaceId],
  );

  // Carga inicial (y cuando cambia el conjunto de widgets persistidos).
  const widgetIdsKey = widgets.map((w) => w.id).join(",");
  useEffect(() => {
    if (widgetIdsKey) void refresh();
  }, [widgetIdsKey, refresh]);

  // Un intervalo por widget con refreshSeconds propio.
  const refreshKey = widgets
    .map((w) => `${w.id}:${w.refreshSeconds ?? ""}`)
    .join(",");
  useEffect(() => {
    const timers = widgetsRef.current
      .filter((w) => (w.refreshSeconds ?? 0) >= 5)
      .map((w) =>
        window.setInterval(
          () => void refresh([w.id]),
          (w.refreshSeconds as number) * 1000,
        ),
      );
    return () => timers.forEach((t) => window.clearInterval(t));
  }, [refreshKey, refresh]);

  return { dataById, refreshingIds, refresh };
}
